import { Fetch } from "@lucaswickstrom/fetch";
import {
  useMutationWithAuthError,
  useQueryWithAuthError,
} from "@ssf/auth-components";
import type { MutationOptions, QueryOptions } from "@ssf/components";
import { queryClient } from "@ssf/components";
import type { CheckValuesType } from "@ssf/memdb-api";
import type {
  Database,
  Document,
  Link,
  Signature,
  SignatureBiometrics,
} from "@ssf/sign-worker";
import { createClient } from "@supabase/supabase-js";
import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { useQueryWrapper } from "./api.js";

export type Section = "signMembership";

const SIGN_API_URL = process.env.SIGN_API_URL;
if (!SIGN_API_URL) {
  throw new Error("env `SIGN_API_URL` is not set");
}

export const signApi = new Fetch({ baseUrl: SIGN_API_URL });
let globalAuthorization = "";
export const supabase = createClient<Database>(SIGN_API_URL, "placeholder", {
  global: {
    fetch(input, init) {
      const headers = new Headers(init?.headers);
      headers.set("authorization", globalAuthorization);
      // @ts-ignore
      return fetch(input, { ...init, headers });
    },
  },
});

export function setAuthorization(access_token?: string) {
  const Authorization = access_token ? `Bearer ${access_token}` : "";
  signApi.options = {
    ...signApi.options,
    headers: Authorization ? { Authorization } : {},
  };
  globalAuthorization = Authorization;
}

export async function sign({
  file,
  biometrics,
  ...data
}: {
  file: File | Blob;
  type: "signature" | "file";
  biometrics?: SignatureBiometrics;
} & ({ linkId: string } | { clubId: number; memberId: number | number[] })) {
  const body = new FormData();
  body.append("file", file);
  if (biometrics) {
    body.append("biometrics", JSON.stringify(biometrics));
  }
  Object.entries(data).forEach(([key, value]) =>
    Array.isArray(value)
      ? value.forEach((item) => body.append(key, String(item)))
      : body.append(key, String(value)),
  );

  return signApi.post("linkId" in data ? "/sign-with-link" : "/sign", {
    body,
  });
}

export const useSign = (options?: MutationOptions<typeof sign>) =>
  useMutationWithAuthError(["useSign"], sign, options);

export const documentsInQuery = (...documentIds: (string | undefined)[]) =>
  queryOptions({
    queryKey: ["useDocumentsIn", ...documentIds],
    queryFn: async () => {
      const { data } = await supabase
        .from("documents")
        .select("*,signatures(*)")
        .in("id", documentIds)
        .throwOnError();
      return data;
    },
    enabled:
      documentIds.filter((documentId) => documentId !== undefined).length > 0,
  });

const addLink = async (
  links: (Pick<Link, "expiresAt" | "sentTo"> & {
    clubId: number;
    memberId: number;
  })[],
) => Promise.all(links.map((body) => signApi.post("/generate-link", { body })));
export const useAddLink = (options?: MutationOptions<typeof addLink>) =>
  useMutationWithAuthError(["useAddLink"], addLink, {
    ...options,
    onSettled(...args) {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: queryKeyUseLinks });
    },
  });

const getLink = (id: string) =>
  signApi.get<
    | (Link & { document?: Document & { signatures: Signature[] } })
    | Pick<Link, "consumed">
    | Pick<Link, "expiresAt">
  >(`/link/${id}`);
export const useLink = (id: string, options?: QueryOptions<typeof getLink>) =>
  useQueryWithAuthError(["useLink", id], () => getLink(id), options);

const supabaseLinks = () =>
  supabase.from("links").select("id,expiresAt,consumed,meta,sentTo");
const getLinks = async (
  query?: (builder: typeof supabaseLinks) => ReturnType<typeof supabaseLinks>,
) =>
  (query?.(supabaseLinks) || supabaseLinks())
    .throwOnError()
    .then(({ data }) => data);
const queryKeyUseLinks = ["useLinks"] as const;
export const useLinks = (
  query?: (builder: typeof supabaseLinks) => ReturnType<typeof supabaseLinks>,
  options?: Partial<QueryOptions<typeof getLinks>>,
) => useQueryWithAuthError(queryKeyUseLinks, () => getLinks(query), options);

const addDocument = async (body: {
  title: string;
  content: string;
  signers: { name: string; email: string }[];
  club: { id: number; name: string };
  district: { id: number };
  documentType: CheckValuesType;
}) => signApi.post("/document", { body });
export const useAddDocument = (options?: MutationOptions<typeof addDocument>) =>
  useMutationWithAuthError(["useAddDocument"], addDocument, {
    ...options,
  });

export const removeDocument = ({
  documentId,
  clubId,
}: {
  documentId: string;
  clubId: number;
}) => signApi.delete(`/document/${documentId}/${clubId}`);
export const useRemoveDocument = (
  options?: MutationOptions<typeof removeDocument>,
) =>
  useMutationWithAuthError(["useRemoveDocument"], removeDocument, {
    ...options,
  });

const sendReminder = async (body: { documentId: string; email: string }) =>
  signApi.post("/reminder", { body });
export const useSendReminder = (
  options?: MutationOptions<typeof sendReminder>,
) => useMutationWithAuthError(["useSendReminder"], sendReminder, options);

const changeEmail = async (body: {
  documentId: string;
  email: string;
  newEmail: string;
}) => signApi.post("/change-email", { body });
export const useChangeEmail = (options?: MutationOptions<typeof changeEmail>) =>
  useMutationWithAuthError(["useChangeEmail"], changeEmail, options);

export const removeSignatureMutation = mutationOptions({
  mutationFn: (id: string) => signApi.delete(`/signature/${id}`),
});

const deleteLink = async (linkId: string) => signApi.delete(`/link/${linkId}`);
export const useDeleteLink = (options?: MutationOptions<typeof deleteLink>) =>
  useMutationWithAuthError(["useDeleteLink"], deleteLink, {
    ...options,
    onSettled(...args) {
      options?.onSettled?.(...args);
      queryClient.invalidateQueries({ queryKey: queryKeyUseLinks });
    },
  });

const getMemberSignatures = async ({
  memberId,
  email,
  documentIds = [],
}: {
  memberId: number;
  email: string;
  documentIds?: string[];
}) =>
  supabase
    .from("signatures")
    .select("*,document:documents(*)")
    .or(
      [
        `signatory.eq.${memberId}`,
        `email.eq.${email}`,
        ...documentIds.map((id) => `documentId.eq.${id}`),
      ].join(","),
    )
    .returns<(Signature & { document: Document })[]>()
    .order("signedAt", { ascending: false })
    .then(({ data }) => data);
export const useMemberSignatures = (
  data: Parameters<typeof getMemberSignatures>[0],
  options?: QueryOptions<typeof getMemberSignatures>,
) =>
  useQueryWithAuthError(
    ["useMemberSignatures", data],
    () => getMemberSignatures(data),
    options,
  );

async function getSignaturesByClubId(clubId: number) {
  const { data: docs } = await supabase
    .from("documents")
    .select("id")
    .eq("meta->club->>id", String(clubId))
    .throwOnError();

  if (!docs || docs.length === 0) {
    return [];
  }

  const documentIds = docs.map((doc) => doc.id);

  return supabase
    .from("signatures")
    .select("*,documents(*)")
    .in("documentId", documentIds)
    .throwOnError()
    .then(({ data }) => data);
}

export const useSignaturesByClubId = (
  clubId: number,
  options?: QueryOptions<typeof getSignaturesByClubId>,
) =>
  useQueryWrapper(
    ["useSignaturesByClubId", clubId],
    () => getSignaturesByClubId(clubId),
    options,
  );

export type SignatureValidationResponse = {
  extractedText: string;
  name: string;
  confidence: number;
  isMatch: boolean;
  isReadable: boolean;
};

export type ClusterValidationResponse = {
  files: number;
  clusters: number;
  clusterDetails: Array<{
    size: number;
    filenames: string[];
  }>;
  largestFraction: number;
  threshold_setting: number;
  fraction_setting: number;
};

export type AuditConfig = {
  clusterThreshold: number;
  clusterFraction: number;
  ocrConfidence: number;
  sizeUniformity: number;
  positionUniformity: number;
  ageMin: number;
  ageMax: number;
  embeddingBackend: string;
};

export type WarningType =
  | "suspicious_cluster"
  | "suspicious_biometrics"
  | "illegible_signatures"
  | "uniform_signature_sizes"
  | "uniform_signature_positions"
  | "multiple_accountants"
  | "address_same_as_club"
  | "accountant_same_address"
  | "accountant_same_lastname"
  | "too_young"
  | "too_old"
  | "name_not_found_in_scan"
  | "protocol_signer_missing"
  | "protocol_signer_mismatch"
  | "protocol_wrong_type"
  | "protocol_wrong_year"
  | "unexpected_signer"
  | "multiple_roles_same_person"
  | "duplicate_signer_email"
  | "board_role_missing_in_memdb"
  | "board_member_mismatch"
  | "report_wrong_year"
  | "report_wrong_club"
  | "protocol_wrong_club"
  | "report_no_activities"
  | "statutes_updated_no_document"
  | "statutes_date_mismatch"
  | "unsupported_file_format"
  | "missing_document"
  | "not_fully_signed";

export type Member = {
  memberId: number;
  firstname: string;
  lastname: string;
  role?: string;
};

export type Warning = {
  type: WarningType;
  memberId?: number;
  value?: number;
  members?: Member[];
};

export type Status = "not_analysed" | "pass" | "issues_found";

export type AuditSection = {
  status: Status;
  warnings?: Warning[];
  electedBoard?: Array<{ name: string; role: string }>;
  cacheKey?: string;
};

export type AuditMeetingSection = AuditSection & {
  correctClub?: boolean;
  protocolType?: "annualMeeting" | "constituentMeeting" | "unknown";
};

export type AuditMembershipsSection = AuditSection & {
  clustering?: ClusterValidationResponse;
  scannedFiles?: Array<{
    members: Array<{
      memberId: number;
      found: boolean;
    }>;
  }>;
};

export type AuditStats = {
  memberships: number;
  digitalSignatures: number;
  scannedFiles: number;
  namesOnScannedFiles: number;
  namesFoundInScan: number;
  boardRoles: number;
  annualMeetingSigs: number;
  constituentMeetingSigs: number;
};

export type AuditClubResponse = {
  clubId: number;
  clubName: string;
  year: number;
  config?: AuditConfig;
  stats?: AuditStats;
  board?: AuditSection;
  memberships?: AuditMembershipsSection;
  annualMeeting?: AuditMeetingSection;
  constituentMeeting?: AuditMeetingSection;
  annualReport?: AuditSection;
};

export async function exportClubPdf(
  clubId: number,
  year: number,
): Promise<Blob> {
  const response = await fetch(
    `${SIGN_API_URL}/export/${clubId}/${year}?signatures=true`,
    {
      headers: {
        Authorization: globalAuthorization,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Export request failed: ${response.status} ${response.statusText} - ${errorText}`,
    );
  }

  return response.blob();
}

export async function auditClub(
  clubId: number,
  year: number,
): Promise<AuditClubResponse> {
  const response = await fetch(`${SIGN_API_URL}/audit/${clubId}/${year}`, {
    headers: {
      Authorization: globalAuthorization,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Club audit request failed: ${response.status} ${response.statusText} - ${errorText}`,
    );
  }

  return response.json() as Promise<AuditClubResponse>;
}

export function auditClubBatch(clubIds: number[], year: number): void {
  fetch(`${SIGN_API_URL}/audit/${year}`, {
    method: "POST",
    headers: {
      Authorization: globalAuthorization,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ clubIds }),
  }).catch(() => {});
}

export function submitAuditVote(params: {
  clubId: number;
  year: number;
  section: string;
  cacheKey: string;
  vote: "up" | "down";
  meta?: Record<string, unknown>;
}): void {
  fetch(`${SIGN_API_URL}/audit/vote`, {
    method: "POST",
    headers: {
      Authorization: globalAuthorization,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  }).catch(() => {});
}

export function deleteAuditVote(params: {
  section: string;
  cacheKey: string;
}): void {
  fetch(`${SIGN_API_URL}/audit/vote`, {
    method: "DELETE",
    headers: {
      Authorization: globalAuthorization,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(params),
  }).catch(() => {});
}

export async function getAuditVotes(
  cacheKeys: string[],
): Promise<Array<{ section: string; vote: "up" | "down"; cacheKey: string }>> {
  if (cacheKeys.length === 0) return [];
  const params = new URLSearchParams();
  for (const key of cacheKeys) {
    params.append("cacheKey", key);
  }
  const response = await fetch(`${SIGN_API_URL}/audit/vote?${params}`, {
    headers: { Authorization: globalAuthorization },
  });
  if (!response.ok) return [];
  return response.json() as Promise<
    Array<{ section: string; vote: "up" | "down"; cacheKey: string }>
  >;
}

const getAvailabilitySetting = async (section: Section) =>
  signApi.get<
    Database["public"]["Tables"]["availabilitySetting"]["Row"] & {
      createdByMember?: { id: number; name: string };
      isAvailable?: boolean;
    }
  >(`/setting/${section}`);

export const useAvailabilitySetting = (
  section: Section,
  options?: QueryOptions<typeof getAvailabilitySetting>,
) =>
  useQueryWithAuthError(
    ["useAvailabilitySetting", section],
    () => getAvailabilitySetting(section),
    options,
  );

const updateAvailabilitySetting = async (body: {
  section: Section;
  status: Database["public"]["Tables"]["availabilitySetting"]["Row"]["status"];
  customMessage?: string;
}) => signApi.post(`/setting/${body.section}`, { body });

export const useUpdateAvailabilitySetting = (
  options?: MutationOptions<typeof updateAvailabilitySetting>,
) =>
  useMutationWithAuthError(
    ["useUpdateAvailabilitySetting"],
    updateAvailabilitySetting,
    options,
  );
